/**
 * Sentry Error Tracking — Frontend
 *
 * Initializes Sentry in production builds. Provides:
 * - Automatic error capture with browser tracing
 * - Masked session replay on errors only when explicitly enabled
 * - Floating "Report a Bug" widget for beta testers
 * - PII stripping (no IP addresses, email, or names)
 *
 * Silently no-ops when VITE_SENTRY_DSN is not set (local dev).
 *
 * Beta → Production transition:
 *   Feedback is opened from app-owned UI with openSentryFeedbackDialog().
 */
import * as Sentry from '@sentry/react';

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return; // No DSN = skip silently (local dev)

  const replayOnErrorEnabled = import.meta.env.VITE_SENTRY_REPLAY_ON_ERROR === 'true';
  const release = typeof __ON_BOOK_RELEASE__ === 'string' && __ON_BOOK_RELEASE__
    ? __ON_BOOK_RELEASE__
    : import.meta.env.VITE_SENTRY_RELEASE;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    ...(release ? { release } : {}),
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),

      // Beta Feedback form. The app owns the visible trigger so beta
      // projects are not dependent on the SDK's auto-injected widget.
      Sentry.feedbackIntegration({
        autoInject: false,
        colorScheme: 'dark',
        buttonLabel: 'Report a Bug',
        submitButtonLabel: 'Send Report',
        formTitle: 'Report a Bug',
        messagePlaceholder: 'What happened? What did you expect? Do not paste scripts, contact lists, schedules, file contents, private rehearsal notes, credentials, or private production details. Screenshots and breadcrumbs may be attached, so keep private material out of the report whenever possible.',
        showBranding: false,
        // Empty keys prevent the SDK default from pre-filling user email/name.
        useSentryUser: { email: '', name: '' },
      }),
    ],
    sendDefaultPii: false,
    tracesSampleRate: 0.2,        // 20% of transactions
    replaysSessionSampleRate: 0,   // No session replays by default
    replaysOnErrorSampleRate: replayOnErrorEnabled ? 1.0 : 0,
    beforeSend(event) {
      // Strip raw user identifiers from automatic captures.
      if (event.user) {
        delete event.user.ip_address;
        delete event.user.email;
        delete event.user.username;
        delete event.user.name;
      }
      return event;
    },
  });
}

type FeedbackDialog = {
  appendToDom: () => void;
  open: () => void;
};

export async function openSentryFeedbackDialog(): Promise<boolean> {
  const feedback = Sentry.getFeedback();
  if (!feedback) return false;

  try {
    const dialog = await feedback.createForm() as FeedbackDialog;
    dialog.appendToDom();
    dialog.open();
    return true;
  } catch {
    return false;
  }
}
